import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '../../../components/inputs/LoadingButton';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { downloadFile } from '@/src/utils/fileUtil';
import { postClaimConsolidatedReport } from '@/src/app/services/Insurance/Claim';
import ClaimsConsolidatedForm from '@/src/app/features/insurance/forms/claimConsolidatedReport/ReportConsolidatedForm';
import { ConsolidatedForm } from '@/src/app/Interfaces/Insurance/Claim';

const ClaimConsolidated = () => {
  const formMethods = useForm<ConsolidatedForm>();
  const { handleSubmit } = formMethods;
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (data: ConsolidatedForm) => {
      return postClaimConsolidatedReport(
        data.companies,
        data.periodStart ?? undefined,
        data.periodEnd ?? undefined,
        data.status === 'all' ? undefined : data.status
      );
    },
    onSuccess: (response) => {
      downloadFile(response);
    },
    onError: (error: AxiosError) => {
      const status = error.status;
      showToast(
        'error',
        toast,
        'Claim Consolidated Report Error',
        status === 404
          ? 'No Claims found!'
          : 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: ConsolidatedForm) => {
    mutate(data);
  };

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-4">
      <div className="text-35px font-bold text-center mb-2 relative">
        Claim Consolidated Report
      </div>
      <FormProvider {...formMethods}>
        <form className="max-w-600px w-full mx-auto flex flex-column gap-5">
          <ClaimsConsolidatedForm />
          <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
            <ConfirmationDialog
              Button={
                <LoadingButton
                  isLoading={isPending}
                  label="Generate"
                  className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                  fontSize="text-22px"
                  type="button"
                />
              }
              onConfirm={handleSubmit(onSubmit)}
              message="Are you sure you want to generate the consolidated report?"
            />
            <Button
              onClick={() => {
                navigate('/insurance/new');
              }}
              className="w-15rem bluwaiRed border-0 flex m-auto py-3"
              type="button"
            >
              <span className="mx-auto text-22px text-white">Cancel</span>
            </Button>
          </div>
        </form>
      </FormProvider>
      <ErrorToast toastRef={toast} />
    </div>
  );
};

export default ClaimConsolidated;
